import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router';

import { useProjectApi } from 'src/services';
import { TitleContainer, ContentContainer } from 'src/components';
import { AnalyticCard } from 'src/scenes/Project/components';
import { AspectCategoryStatusIcon } from 'src/scenes/Project/scenes/PressuresAndDependencies/components';
import { getHighestImpactCategory } from 'src/utils/aspectCategoriesUtils';
import { objectIsEmpty } from 'src/utils/util';


const dependencyCategoriesLang = {
  otherProvisioningServicesAnimalBasedEnergy: 'Otros servicios de aprovisionamiento - energía de origen animal',
  biomassProvisioning: 'Servicios de aprovisionamiento de biomasa',
  solidWasteRemediation: 'Remediación de residuos sólidos',
  soilAndSedimentRetention: 'Servicios de retención de los suelos y sedimentos',
  waterPurification: 'Servicios de purificación del agua',
  soilQualityRegulation: 'Servicios de regulación de la calidad del suelo',
  otherRegulatingAndMaintenanceServiceDilution:
    'Otros servicios de regulación y mantenimiento - Dilución por la atmósfera y los ecosistemas',
  biologicalControl: 'Servicios de control biológico',
  airFiltration: 'Servicios de filtración de aire',
  floodMitigationServices: 'Servicios de mitigación de inundaciones',
  geneticMaterial: 'Servicios de material genético',
  globalClimateRegulation: 'Servicios de regulación climática a nivel global',
  waterSupply: 'Abastecimiento de agua',
  nurseryPopulationAndHabitatMaintenance: 'Servicios de mantenimiento de las poblaciones y los hábitats de cría',
  noiseAttenuation: 'Servicios de atenuación del ruido',
  otherRegulatingAndMaintenanceServiceMediation:
    'Otros servicios de regulación y mantenimiento - Mediación de impactos sensoriales (además del ruido)',
  localClimateRegulation: 'Servicios de regulación del clima a nivel local (micro/meso)',
  pollination: 'Servicios de polinización',
  stormMitigation: 'Servicios de mitigación de las tormentas',
  waterFlowRegulation: 'Servicios de regulación del flujo de agua',
  rainfallPatternRegulation: 'Servicios de regulación del patrón de precipitaciones (escala subcontinental)',
  recreationRelatedServices: 'Servicios de carácter recreativo',
  visualAmenityServices: 'Servicios de estética visual',
  educationScientificAndResearchServices: 'Servicios educativos, científicos y de investigación',
  spiritualArtisticAndSymbolicServices: 'Servicios espirituales, artísticos y simbólicos',
};

const dependencyGroups = [
  {
    id: 'provisioningServices',
    label: 'Servicios de aprovisionamiento',
    categories: [
      'otherProvisioningServicesAnimalBasedEnergy',
      'biomassProvisioning',
      'waterSupply',
      'geneticMaterial',
    ],
  },
  {
    id: 'regulationAndMaintenance',
    label: 'Servicios de regulación y mantenimiento',
    categories: [
      'solidWasteRemediation',
      'soilAndSedimentRetention',
      'waterPurification',
      'soilQualityRegulation',
      'otherRegulatingAndMaintenanceServiceDilution',
      'biologicalControl',
      'airFiltration',
      'floodMitigationServices',
      'globalClimateRegulation',
      'nurseryPopulationAndHabitatMaintenance',
      'noiseAttenuation',
      'otherRegulatingAndMaintenanceServiceMediation',
      'localClimateRegulation',
      'pollination',
      'stormMitigation',
      'waterFlowRegulation',
      'rainfallPatternRegulation',
    ],
  },
  {
    id: 'culturalServices',
    label: 'Servicios culturales',
    categories: [
      'recreationRelatedServices',
      'visualAmenityServices',
      'educationScientificAndResearchServices',
      'spiritualArtisticAndSymbolicServices',
    ],
  },
];


const useStyles = makeStyles(theme => ({
  grid: {
    height: '100%',
  },
  group: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray,
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
}));

const DependenciesContainer = props => {
  const classes = useStyles();

  const match = useRouteMatch();
  const projectApi = useProjectApi();

  const [ isicDependencies, setIsicDependencies ] = useState({});

  const { projectId } = match.params;

  const { handleConfirm: fetchIsicDependencies } = {
    handleConfirm: async () => {
      const { dependencies } = await projectApi.getPressuresAndDependencies(projectId);
      setIsicDependencies(dependencies);
    },
  };

  useEffect(() => {
    fetchIsicDependencies();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const DependenciesCategory = dependencyGroup => {
    if (objectIsEmpty(isicDependencies)) {
      return null;
    }
    const groupValues = dependencyGroup.categories.map(category => isicDependencies[category]);
    const highest = getHighestImpactCategory(groupValues);
    return (
      <Box mt={-1}>
        <AspectCategoryStatusIcon category={highest} />
      </Box>
    );
  };

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        project={props.project}
        breadcrumbs={[ 'projects', 'project' ]}>
        Dependencias
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <AnalyticCard
          title={'Screening'}
        >
          <Divider />
          <Box mt={2} />
          <Grid container spacing={1} >
            {dependencyGroups.map(dependencyGroup =>
              <Grid key={dependencyGroup.id} item xs={4} className={classes.grid}>
                <Box className={classes.group}>
                  <Box display="flex" mb={0.5} alignItems="center">
                    <Typography variant="body2" className={classes.subtitle}>{dependencyGroup.label}</Typography>
                    <Box flexGrow={1} />
                    {DependenciesCategory(dependencyGroup)}
                  </Box>
                  <Divider />
                  <Box mt={1} />
                  {dependencyGroup.categories.map(category =>
                    <Box key={category} display="flex" alignItems="center" py={0.5} mr={0.5}>
                      <Typography variant="body2" color="textPrimary">
                        {dependencyCategoriesLang[category]}
                      </Typography>
                      <Box flexGrow={1} />
                      <AspectCategoryStatusIcon category={isicDependencies[category]} size="small" />
                    </Box>,
                  )}
                </Box>
              </Grid>,
            )}
          </Grid>
        </AnalyticCard>
      </ContentContainer>
    </Grid>
  );
};

DependenciesContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  DependenciesContainer,
};
